import { createSlice } from "@reduxjs/toolkit";
import {
    startUILoading,
    stopUILoading,
} from "../../actions/administrador/ui/ui";
import {
    Call,
    CallWithFormData,
    CallWithFormDataFile,
    downloadBlob,
    objectToFormData,
} from "../../../helpers/fetch";
import { errorHandler } from "../../../helpers/errorHandler";
import Swal from "sweetalert2";
import { startStudentSetActive } from "../../actions/students/students";
import { objectEquals } from "@helpers/objectEquals";

const initialState = {
    pagination: {
        data: [],
        per_page: 50,
    },
    paginationPending: {
        data: [],
        per_page: 50,
    },
    showModal: false,
    showReviewModal: false,
    active: null,
    report: {
        pagination: {
            data: [],
            per_page: 10,
            total: 0,
        },
    },
    condensed: null,
};

export const statusChange = createSlice({
    name: "statusChange",
    initialState,
    reducers: {
        setSetActive: (state, action) => {
            state.active = action.payload;
        },
        startUnsetActive: (state) => {
            state.active = null;
        },
        startSetDocUri: (state, action) => {
            state.active.document_status_uri =
                action.payload.document_status_uri;
        },
        openModal: (state) => {
            state.showModal = true;
        },
        closeModal: (state) => {
            state.showModal = false;
        },
        openReviewModal: (state) => {
            state.showReviewModal = true;
        },
        closeReviewModal: (state) => {
            state.showReviewModal = false;
        },
        setPagination: (state, action) => {
            state.pagination = objectEquals(state.pagination, action.payload);
        },
        setPaginationPending: (state, action) => {
            state.paginationPending = objectEquals(
                state.paginationPending,
                action.payload
            );
        },
        setPaginationReport: (state, action) => {
            state.report.pagination = objectEquals(
                state.report.pagination,
                action.payload
            );
        },
        setSignUriPagination: (state, action) => {
            const target = state.pagination.data.find(
                (item) => item.id === action.payload.id
            );

            if (target) {
                target.sign_uri = action.payload.sign_uri;
            }
        },

        setSignUriPaginationReport: (state, action) => {
            const target = state.report.pagination.data.find(
                (item) => item.id === action.payload.id
            );

            if (target) {
                target.sign_uri = action.payload.sign_uri;
            }
        },
        clearPaginationReport: (state) => {
            state.report.pagination = initialState.report.pagination;
        },
        seListReport: (state, action) => {
            state.report.list = action.payload;
        },
        setCondensed: (state, action) => {
            state.condensed = action.payload;
        },
    },
});

export const {
    setSetActive,
    startUnsetActive,
    startSetDocUri,
    openModal,
    closeModal,
    openReviewModal,
    closeReviewModal,
    setPagination,
    setSignUriPagination,
    setPaginationPending,
    setPaginationReport,
    setSignUriPaginationReport,
    clearPaginationReport,
    seListReport,
    setCondensed,
} = statusChange.actions;

export default statusChange.reducer;

// Actions
export const startShow = (values) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const {
            exportFlag,
            page,
            query,
            number_rows,
            request_status,
            review_status,
            approval_status,
            cycle_request,
            cycle_application,
            request_date,
            review_date,
            coordination_ids,
            curriculum_ids,
        } = values;

        const params = new URLSearchParams();

        if (exportFlag) {
            params.set("export", exportFlag);
        } else {
            params.set("page", page);
            params.set("q", query);
            params.set("per_page", number_rows);
        }

        if (request_status) {
            params.set("request_status", request_status);
        }

        if (review_status) {
            params.set("review_status", review_status);
        }

        if (approval_status) {
            params.set("approval_status", approval_status);
        }

        if (cycle_request) {
            params.set(
                "cycle_request",
                `${cycle_request.year}-${cycle_request.sub_cycle}`
            );
        }

        if (cycle_application) {
            params.set(
                "cycle_application",
                `${cycle_application.year}-${cycle_application.sub_cycle}`
            );
        }

        if (request_date.from && request_date.to) {
            params.set("request_date_from", request_date.from);
            params.set("request_date_to", request_date.to);
        }

        if (review_date.from && review_date.to) {
            params.set("review_date_from", review_date.from);
            params.set("review_date_to", request_date.to);
        }

        if (coordination_ids) {
            params.set(
                "coordination_ids",
                coordination_ids?.map((s) => s.value)?.join(",")
            );
        }
        if (curriculum_ids) {
            params.set(
                "curriculum_ids",
                curriculum_ids?.map((s) => s.value)?.join(",")
            );
        }

        let resp;
        if (exportFlag) {
            resp = await CallWithFormDataFile(
                `operators/students/statuses`,
                "GET",
                params.toString()
            );

            downloadBlob(
                resp,
                "Reporte de revisión de estatus académico de estudiantes"
            );
        } else {
            resp = await Call(`operators/students/statuses`, "GET", params);

            if (resp.success) {
                dispatch(setPagination(resp.success));
            } else {
                errorHandler(resp);
            }
        }

        dispatch(stopUILoading());
    };
};

export const startShowPending = (academic_record, values) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const { page, query, number_rows } = values;

        const params = new URLSearchParams();
        params.set("page", page);
        params.set("q", query);
        params.set("per_page", number_rows);
        params.set("request_statuses", "PENDIENTE");

        const resp = await Call(
            `operators/students/statuses/${academic_record}`,
            "GET",
            params
        );

        if (resp.success) {
            dispatch(setPaginationPending(resp.success));
        } else {
            errorHandler(resp);
        }
        dispatch(stopUILoading());
    };
};

export const startShowHistorial = (academic_record, values) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const { page, query, number_rows } = values;

        const params = new URLSearchParams();
        params.set("page", page);
        params.set("q", query);
        params.set("per_page", number_rows);
        params.set("request_statuses", "ACEPTADO");

        const resp = await Call(
            `operators/students/statuses/${academic_record}`,
            "GET",
            params
        );

        if (resp.success) {
            dispatch(setPagination(resp.success));
        } else {
            errorHandler(resp);
        }
        dispatch(stopUILoading());
    };
};

export const startSave = (
    academic_record,
    values,
    callback = () => {},
    close = () => {}
) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const formData = objectToFormData(values);

        if (values.category_low) {
            formData.append(
                "category_low",
                values.category_low.map((c) => c.value).join(",")
            );
        }

        const resp = await CallWithFormData(
            `operators/students/statuses/${academic_record.id}`,
            "POST",
            formData
        );

        if (resp.success) {
            Swal.fire({
                title: "Éxito",
                text: "Solicitud creada.",
                icon: "success",
                toast: true,
                position: "bottom-end",
                timer: 8000,
            });

            callback();
            close();
        } else {
            errorHandler(resp);
        }
        dispatch(stopUILoading());
    };
};

export const startReview = (
    mode,
    academic_record,
    academic_record_status,
    values,
    callback = () => {},
    close = () => {}
) => {
    return async (dispatch) => {
        if (values.request_status === "ACEPTADO" && mode === "APPROVAL") {
            const result = await Swal.fire({
                title: "¿Estás seguro?",
                html: `Estás a punto de aprobar la solicitud, esta acción cambiará el estatus del alumno a:<br><br>
                       <strong>"${academic_record_status.status}"</strong>.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Aprobar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: "#2e4a6c",
                cancelButtonColor: "#6c757d",
                reverseButtons: true,
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        dispatch(startUILoading());

        const resp = await Call(
            `operators/students/statuses/review/${academic_record_status.id}`,
            "post",
            { mode, ...values }
        );

        if (resp.success) {
            if (values.request_status === "ACEPTADO") {
                dispatch(
                    startStudentSetActive({
                        ...academic_record,
                        status: resp.success.status,
                        moodle: resp.success?.academic_record?.moodle,
                    })
                );
            }

            Swal.fire({
                title: "Éxito",
                text:
                    mode === "APPROVAL"
                        ? "Solicitud aprobada."
                        : "Solicitud revisada.",
                icon: "success",
                toast: true,
                position: "bottom-end",
                timer: 8000,
            });

            callback();
            close();
        } else {
            errorHandler(resp);
        }
        dispatch(stopUILoading());
    };
};

export const startShowReport = ({
    page = 1,
    query = "",
    number_rows = 10,
    asList = 0,
    cycle_application,
    period,
    only_latest,
    status,
    coordination_ids,
    curriculum_ids,
}) => {
    return async (dispatch) => {
        if (!asList) {
            dispatch(startUILoading());
        }

        const params = new URLSearchParams();

        params.set(
            "cycle_application",
            `${cycle_application.year}-${cycle_application.sub_cycle}`
        );

        if (period) {
            params.set("period", period);
        }

        if (status) {
            params.set("status", status?.map((s) => s.value)?.join(","));
        }

        params.set("only_latest", only_latest ? 1 : 0);

        if (coordination_ids) {
            params.set(
                "coordination_ids",
                coordination_ids?.map((s) => s.value)?.join(",")
            );
        }
        if (curriculum_ids) {
            params.set(
                "curriculum_ids",
                curriculum_ids?.map((s) => s.value)?.join(",")
            );
        }

        if (asList) {
            params.set("asList", asList);
        } else {
            params.set("page", page);
            params.set("q", query);
            params.set("per_page", number_rows);
        }

        const resp = await Call(
            `operators/students/statuses/report`,
            "GET",
            params
        );

        if (resp.success) {
            dispatch(setPaginationReport(resp.success));
        } else {
            errorHandler(resp);
        }

        if (!asList) {
            dispatch(stopUILoading());
        }
    };
};

export const startExportReport = ({
    cycle_application,
    period,
    only_latest,
    status,
    coordination_ids,
    curriculum_ids,
}) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const params = new URLSearchParams();

        params.set("export", 1);

        params.set(
            "cycle_application",
            `${cycle_application.year}-${cycle_application.sub_cycle}`
        );

        if (period) {
            params.set("period", period);
        }

        params.set("only_latest", only_latest ? 1 : 0);

        if (status?.length > 0) {
            params.set("status", status?.map((s) => s.value)?.join(","));
        }
        if (coordination_ids?.length > 0) {
            params.set(
                "coordination_ids",
                coordination_ids?.map((s) => s.value)?.join(",")
            );
        }
        if (curriculum_ids?.length > 0) {
            params.set(
                "curriculum_ids",
                curriculum_ids?.map((s) => s.value)?.join(",")
            );
        }

        const response = await CallWithFormDataFile(
            `operators/students/statuses/report`,
            "GET",
            params.toString()
        );
        downloadBlob(response, "Reporte de estatus académico de estudiantes");

        dispatch(stopUILoading());
    };
};

export const startShowCondensed = ({
    cycle_application,
    period,
    only_latest,
    status,
}) => {
    return async (dispatch) => {
        dispatch(startUILoading());

        const params = new URLSearchParams();

        params.set("isCondensed", 1);

        params.set(
            "cycle_application",
            `${cycle_application.year}-${cycle_application.sub_cycle}`
        );

        if (period) {
            params.set("period", period);
        }

        params.set("only_latest", only_latest ? 1 : 0);

        if (status) {
            params.set("status", status);
        }

        const resp = await Call(
            `operators/students/statuses/report`,
            "GET",
            params
        );

        if (resp.success) {
            dispatch(setCondensed(resp.success));
        } else {
            errorHandler(resp);
        }

        dispatch(stopUILoading());
    };
};

export const startGetSignUri = (status_id, targetPagination = "pagination") => {
    return async (dispatch) => {
        const resp = await Call(
            `operators/students/statuses/get-sign-uri/${status_id}`,
            "GET"
        );

        if (resp.success) {
            if (targetPagination == "pagination") {
                dispatch(setSignUriPagination(resp.success));
            } else if (targetPagination == "report") {
                dispatch(setSignUriPaginationReport(resp.success));
            }
        } else {
            errorHandler(resp);
        }
    };
};

export const startGetDocUri = (status_id) => {
    return async (dispatch) => {
        const resp = await Call(
            `operators/students/statuses/get-doc-uri/${status_id}`,
            "GET"
        );

        if (resp.success) {
            dispatch(startSetDocUri(resp.success));
        } else {
            errorHandler(resp);
        }
    };
};
